import { useState } from "react";
import { MessageCircle, HelpCircle, ChevronDown, ChevronUp } from "lucide-react";
import SupportEmailButton from "@/components/SupportEmailButton";
import { useLanguage } from "@/context/LanguageContext";
import { openContactModal } from "@/components/ContactModal";

const SUPPORT_EMAIL = "bookings@karinelauzon.com";
const MESSENGER_URL = "https://m.me/PreciseRNInjectionist";

/**
 * Collapsible "Need help?" strip for the booking flow.
 *
 * Iter 453 · Feb 2026 · Karine ask — the always-visible support panel
 * was cluttering step 1/2 and confusing novice users by competing for
 * attention with the primary "Pick your treatment" CTA. Now collapsed
 * to a small link by default; the Messenger/Email buttons only appear
 * once the user explicitly opens the drawer.
 */
export default function BookingHelpStrip({ className = "" }) {
  const { language } = useLanguage();
  const isFr = language === "fr";
  const [open, setOpen] = useState(false);

  const subject = isFr ? "Aide avec ma réservation" : "Help with my booking";
  const body = isFr
    ? "Bonjour,\n\nJe rencontre un problème pour finaliser ma réservation. Voici ce que j'essayais de faire :\n\n[décrire le problème ici]\n\nMerci !"
    : "Hi,\n\nI'm having trouble completing my booking. Here's what I was trying to do:\n\n[describe the issue here]\n\nThanks!";

  return (
    <div className={className} data-testid="booking-help-strip">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 text-[11px] sm:text-xs text-zinc-400 hover:text-amber-300 transition-colors"
        data-testid="booking-help-toggle"
      >
        <HelpCircle className="w-3.5 h-3.5" />
        <span className="underline-offset-2 hover:underline">
          {isFr ? "Besoin d'aide ?" : "Need help?"}
        </span>
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>

      {open && (
        <div
          className="mt-2 bg-zinc-800/40 border border-zinc-700/40 rounded-lg px-3 py-2.5 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 animate-in fade-in slide-in-from-top-1 duration-200"
          data-testid="booking-help-panel"
        >
          <p className="text-xs text-zinc-400 leading-snug flex-1 min-w-0">
            {isFr ? (
              <>
                <span className="text-amber-400 font-semibold">Bloqué·e sur une étape ?</span>{" "}
                Écrivez-nous et Karine ou son équipe vous répondront rapidement.
              </>
            ) : (
              <>
                <span className="text-amber-400 font-semibold">Stuck on a step?</span>{" "}
                Reach out and Karine or her team will get back to you fast.
              </>
            )}
          </p>
          <div className="flex items-center gap-2 flex-shrink-0">
            <a
              href={MESSENGER_URL}
              onClick={(e) => { e.preventDefault(); openContactModal({ source: "messenger-cta" }); }}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="booking-help-messenger"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-[#0084FF]/10 hover:bg-[#0084FF]/20 border border-[#0084FF]/40 text-[#67b6ff] text-xs font-semibold transition-colors"
            >
              <MessageCircle className="w-3.5 h-3.5" />
              {isFr ? "Messenger" : "Messenger"}
            </a>
            <SupportEmailButton
              email={SUPPORT_EMAIL}
              subject={subject}
              body={body}
              label={isFr ? "Courriel" : "Email"}
              data-testid="booking-help-email"
              className="px-2.5 py-1.5 rounded-md"
            />
          </div>
        </div>
      )}
    </div>
  );
}
